import React, { useMemo, useCallback, useRef, useState, useEffect, startTransition } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { HomeChatProvider } from './HomeChatContext';
import {
  useHomeChatAgent,
  useHomeChatConversations,
  useHomeChatMessages,
  useDeleteConversation,
  useHomeChatStreaming,
  useDocumentPanel,
  useChatDocuments,
  useCreateDocumentFromMessage,
  useChatModelSelector,
} from '../hooks';
import { useContextOrchestrator } from '../hooks/useContextOrchestrator';
import { useDocumentOrchestrator } from '../hooks/useDocumentOrchestrator';
import { useGlobalChatOrchestrator } from '../hooks/useGlobalChatOrchestrator';
import { useStore } from '../../../../hooks';
import { DEFAULT_CHAT_PROVIDERS } from '../constants/chatModelConstants';
import { getDefaultAgentForNewChat } from '../utils/agentUtils';

/**
 * Wrapper that uses the hooks and feeds the Context with real state.
 * WebSocket streaming (DELTA/COMPLETE) is handled by useHomeChatStreaming via websocketService.
 * Must wrap the HomePageV2 layout.
 *
 * Architecture:
 * - useContextOrchestrator  → context injection (Memoria operativa del chat)
 * - useDocumentOrchestrator → document panel lifecycle and chat-document tracking
 * - useGlobalChatOrchestrator → Global Chat Panel state machine (CLOSED/OPEN_GLOBAL/OPEN_HOST)
 */
export const HomeChatProviderWrapper = ({ children }) => {
  const { user: auth0User } = useAuth0();
  const { authStore } = useStore();

  const agentHook = useHomeChatAgent();
  const conversationsHook = useHomeChatConversations();
  const messagesHook = useHomeChatMessages();

  /** When set, fetchMessages for this conversation are skipped within SKIP_FETCH_WINDOW_MS so we keep optimistic message and socket updates. */
  const SKIP_FETCH_WINDOW_MS = 500;
  const skipFetchConversationIdRef = useRef(null);
  const skipFetchTimestampRef = useRef(0);

  const deleteHook = useDeleteConversation({
    selectConversation: conversationsHook.selectConversation,
    fetchConversations: conversationsHook.fetchConversations,
    selectedConversationId: conversationsHook.selectedConversationId,
    selectedAgentId: agentHook.selectedAgentId,
  });

  const documentPanelHook = useDocumentPanel();
  const chatDocumentsHook = useChatDocuments();
  const { createDocumentFromMessage } = useCreateDocumentFromMessage();

  const lastSendTimeRef = useRef(null);
  const modelSelector = useChatModelSelector(DEFAULT_CHAT_PROVIDERS, { lastSendTimeRef });

  // ─── Domain orchestrators ──────────────────────────────────────────────────────
  const contextOrchestrator = useContextOrchestrator(authStore, auth0User);
  const {
    latestContextEntryRef,
    pushContext,
    removeContext,
    clearContext,
    syncContextEntry,
    contextForPayload,
    activeScope,
    getDerivedContextFromPath,
    buildContextPayloadForSend,
  } = contextOrchestrator;

  const documentOrchestrator = useDocumentOrchestrator({
    agentHook,
    conversationsHook,
    chatDocumentsHook,
    documentPanelHook,
    createDocumentFromMessage,
  });
  const { openDocumentPanel, closeDocumentPanel, documentCreatedMessageIdsByConversation, removeDocumentCreatedMessageId } = documentOrchestrator;

  const globalChatOrchestrator = useGlobalChatOrchestrator({ conversationsHook, messagesHook });
  const { isGlobalChatOpen, openGlobalChat, closeGlobalChat, dismissGlobalChat, registerChatHost, unregisterChatHost } = globalChatOrchestrator;

  // ─── Streaming state (25+ event types) ────────────────────────────────────────
  const [currentActivity, setCurrentActivity] = useState(null);
  const [toolCalls, setToolCalls] = useState([]);
  const [hitlRequest, setHitlRequest] = useState(null);
  const [sources, setSources] = useState([]);
  const [uiBlocks, setUiBlocks] = useState([]);
  const [messageStatus, setMessageStatus] = useState(null);
  const [planSteps, setPlanSteps] = useState([]);
  const [isStreaming, setIsStreaming] = useState(false);

  // Keep a ref to current messages so the streaming hook can check for ID collisions
  // without re-subscribing to the WebSocket on every message update.
  const messagesRef = useRef(messagesHook.messages);
  messagesRef.current = messagesHook.messages;

  useHomeChatStreaming({
    conversationId: conversationsHook.conversationId,
    setMessages: messagesHook.setMessages,
    messagesRef,
    setAgentIsTyping: messagesHook.setAgentIsTyping,
    setCurrentActivity,
    setToolCalls,
    setHitlRequest,
    setSources,
    setUiBlocks,
    setMessageStatus,
    setPlanSteps,
    setIsStreaming,
  });

  // Fetch agents exactly once when the provider mounts (global-level guard).
  const agentsFetchedRef = useRef(false);
  useEffect(() => {
    if (agentsFetchedRef.current) return;
    agentsFetchedRef.current = true;
    agentHook.fetchAgents();
  }, [agentHook.fetchAgents]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectAgentWithClearConversation = useCallback(
    (agentId, agentData) => {
      conversationsHook.clearConversationSelection();
      messagesHook.cleanThread();
      agentHook.selectAgent(agentId, agentData);
    },
    [conversationsHook, messagesHook, agentHook]
  );

  // When send message returns a conversation_uuid, set it and add to list.
  // Skip the next fetchMessages for this id so we don't wipe the optimistic message.
  const sendMessageWithConversationUpdate = useCallback(
    async (messageText, options = {}) => {
      lastSendTimeRef.current = Date.now();

      const llmConfig =
        options.llmConfig &&
        typeof options.llmConfig === 'object' &&
        options.llmConfig.llm_model
          ? options.llmConfig
          : modelSelector.selectedProvider && modelSelector.selectedModel
            ? {
                llm_model: modelSelector.selectedModel,
                llm_model_provider: modelSelector.selectedProvider,
              }
            : undefined;
      const { llmConfig: _omit, ...restOptions } = options;

      const fromRef =
        latestContextEntryRef.current && latestContextEntryRef.current.key
          ? (() => {
              const { key, ...rest } = latestContextEntryRef.current;
              const merged = { ...rest };
              if (!merged.location || typeof merged.location !== 'object') {
                merged.location = { page: '', section: '' };
              }
              return merged;
            })()
          : undefined;

      const derived = fromRef ?? getDerivedContextFromPath();
      const effectiveContext =
        contextForPayload ?? (derived ? buildContextPayloadForSend(derived) : null);

      // TODO(COPILOT): Remove fallback when backend supports synthetic Copilot UUID.
      let effectiveAgentId = restOptions.agentId ?? agentHook.selectedAgentId;
      if (effectiveAgentId && String(effectiveAgentId).startsWith('copilot-')) {
        const { id: defaultId } = getDefaultAgentForNewChat(agentHook.agents ?? []);
        effectiveAgentId = defaultId ?? effectiveAgentId;
      }

      const response = await messagesHook.sendMessage(messageText, {
        ...restOptions,
        agentId: effectiveAgentId,
        ...(llmConfig && { llmConfig }),
        context: effectiveContext ?? undefined,
      });

      const conversationUuid =
        response?.body?.conversation_uuid ?? response?.conversation_uuid ?? restOptions.conversationId;
      if (conversationUuid) {
        skipFetchConversationIdRef.current = conversationUuid;
        skipFetchTimestampRef.current = Date.now();
        conversationsHook.setSelectedConversationId(conversationUuid);
        conversationsHook.setConversationId(conversationUuid);
        const existing = conversationsHook.conversations?.some(
          (c) => (c.uuid || c.id || c.conversationUuid) === conversationUuid
        );
        if (!existing) {
          conversationsHook.addConversation({
            id: conversationUuid,
            uuid: conversationUuid,
            conversationUuid,
            conversation_uuid: conversationUuid,
            title: (typeof messageText === 'string' ? messageText : '').slice(0, 40) || 'New Conversation',
            last_modified: new Date().toISOString(),
            startedAt: new Date().toISOString(),
            agent_id: options.agentId || agentHook.selectedAgentId,
            participants: [],
          });
        }
      }
      return response;
    },
    [
      messagesHook,
      conversationsHook,
      agentHook.selectedAgentId,
      agentHook.agents,
      modelSelector.selectedProvider,
      modelSelector.selectedModel,
      contextForPayload,
      getDerivedContextFromPath,
      buildContextPayloadForSend,
    ]
  );

  const fetchMessagesWithSkip = useCallback(
    (conversationId) => {
      if (!conversationId) return;
      // Skip: optimistic send window (avoid wiping socket updates)
      if (
        conversationId === skipFetchConversationIdRef.current &&
        Date.now() - skipFetchTimestampRef.current < SKIP_FETCH_WINDOW_MS
      ) {
        return;
      }
      skipFetchConversationIdRef.current = null;
      // Skip: already have messages for this conversation (e.g. navigated panel → UCP)
      const current = messagesRef.current;
      if (
        Array.isArray(current) &&
        current.length > 0 &&
        current[0]?.conversationId === conversationId
      ) {
        return;
      }
      return messagesHook.fetchMessages(conversationId);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: messagesHook reference changes every render
    [messagesHook.fetchMessages]
  );

  const selectConversationWithLoading = useCallback(
    (id) => {
      const isChanging = id !== conversationsHook.selectedConversationId;
      if (id && isChanging) {
        startTransition(() => {
          messagesHook.setIsLoadingMessages(true);
        });
      }
      conversationsHook.selectConversation(id);
    },
    [conversationsHook, messagesHook]
  );

  const { agent: defaultAgentForNewChat, id: defaultAgentIdForNewChat } = useMemo(() => {
    const agents = agentHook.agents;
    if (activeScope) {
      const copilotId = `copilot-${activeScope}`;
      return {
        agent: { id: copilotId, uuid: copilotId, name: 'Copilot', scope: activeScope, isCopilot: true },
        id: copilotId,
      };
    }
    if (!agents?.length) return { agent: null, id: null };
    return getDefaultAgentForNewChat(agents);
  }, [agentHook.agents, activeScope]);

  const [isConversationSearchOpen, setIsConversationSearchOpen] = useState(false);
  const openConversationSearch = useCallback(() => setIsConversationSearchOpen(true), []);
  const closeConversationSearch = useCallback(() => setIsConversationSearchOpen(false), []);

  const [isSlideOutMode, setIsSlideOutMode] = useState(false);

  // ─── Actions context value (stable refs — re-renders rarely) ──────────────────
  const actionsValue = useMemo(
    () => ({
      // agents
      fetchAgents: agentHook.fetchAgents,
      selectAgent: selectAgentWithClearConversation,
      clearAgent: agentHook.clearAgent,
      getAgentById: agentHook.getAgentById,

      // conversations
      fetchConversations: conversationsHook.fetchConversations,
      selectConversation: selectConversationWithLoading,
      clearConversationSelection: conversationsHook.clearConversationSelection,
      addConversation: conversationsHook.addConversation,
      createNewConversation: conversationsHook.createNewConversation,
      deleteConversation: deleteHook.deleteConversation,
      setConversations: conversationsHook.setConversations,
      setSelectedConversationId: conversationsHook.setSelectedConversationId,
      setConversationId: conversationsHook.setConversationId,
      setLastConversationId: conversationsHook.setLastConversationId,
      setIsLoadingConversations: conversationsHook.setIsLoadingConversations,

      // messages
      fetchMessages: fetchMessagesWithSkip,
      sendMessage: sendMessageWithConversationUpdate,
      cleanThread: messagesHook.cleanThread,
      setMessages: messagesHook.setMessages,
      setAgentIsTyping: messagesHook.setAgentIsTyping,
      setIsLoadingMessages: messagesHook.setIsLoadingMessages,
      setError: messagesHook.setError,

      // document panel
      openDocumentPanel,
      closeDocumentPanel,
      consumeLastOpenedFromMessageId: documentPanelHook.consumeLastOpenedFromMessageId,
      setSidebarCollapsed: documentPanelHook.setSidebarCollapsed,
      addDocument: chatDocumentsHook.addDocument,
      updateDocument: chatDocumentsHook.updateDocument,
      deleteDocument: chatDocumentsHook.deleteDocument,
      getDocument: chatDocumentsHook.getDocument,
      setCurrentDocument: chatDocumentsHook.setCurrentDocument,
      clearCurrentDocument: chatDocumentsHook.clearCurrentDocument,
      searchDocuments: chatDocumentsHook.searchDocuments,
      removeDocumentCreatedMessageId,

      // model selector
      onModelChange: modelSelector.onModelChange,
      onProviderChange: modelSelector.onProviderChange,

      // conversation search
      openConversationSearch,
      closeConversationSearch,

      // slide-out
      setIsSlideOutMode,

      // global chat trigger
      openGlobalChat,
      closeGlobalChat,
      dismissGlobalChat,
      registerChatHost,
      unregisterChatHost,

      // context injection (Story 7)
      pushContext,
      removeContext,
      clearContext,
      syncContextEntry,

      // ui placeholders
      setAttachment: () => {},
      setPersistentFileUuids: () => {},

      // streaming
      setCurrentActivity,
      setToolCalls,
      setHitlRequest,
      setSources,
      setUiBlocks,
      setMessageStatus,
      setPlanSteps,
      setIsStreaming,
    }),
    [
      selectAgentWithClearConversation,
      selectConversationWithLoading,
      sendMessageWithConversationUpdate,
      fetchMessagesWithSkip,
      openDocumentPanel,
      closeDocumentPanel,
      removeDocumentCreatedMessageId,
      openConversationSearch,
      closeConversationSearch,
      openGlobalChat,
      closeGlobalChat,
      dismissGlobalChat,
      registerChatHost,
      unregisterChatHost,
      pushContext,
      removeContext,
      clearContext,
      syncContextEntry,
      // agentHook actions
      agentHook.fetchAgents,
      agentHook.clearAgent,
      agentHook.getAgentById,
      // conversationsHook actions
      conversationsHook.fetchConversations,
      conversationsHook.clearConversationSelection,
      conversationsHook.addConversation,
      conversationsHook.createNewConversation,
      conversationsHook.setConversations,
      conversationsHook.setSelectedConversationId,
      conversationsHook.setConversationId,
      conversationsHook.setLastConversationId,
      conversationsHook.setIsLoadingConversations,
      // deleteHook actions
      deleteHook.deleteConversation,
      // messagesHook actions
      messagesHook.cleanThread,
      messagesHook.setMessages,
      messagesHook.setAgentIsTyping,
      messagesHook.setIsLoadingMessages,
      messagesHook.setError,
      // documentPanelHook actions
      documentPanelHook.consumeLastOpenedFromMessageId,
      documentPanelHook.setSidebarCollapsed,
      // chatDocumentsHook actions
      chatDocumentsHook.addDocument,
      chatDocumentsHook.updateDocument,
      chatDocumentsHook.deleteDocument,
      chatDocumentsHook.getDocument,
      chatDocumentsHook.setCurrentDocument,
      chatDocumentsHook.clearCurrentDocument,
      chatDocumentsHook.searchDocuments,
      // modelSelector actions
      modelSelector.onModelChange,
      modelSelector.onProviderChange,
      // streaming actions
      setCurrentActivity,
      setToolCalls,
      setHitlRequest,
      setSources,
      setUiBlocks,
      setMessageStatus,
      setPlanSteps,
      setIsStreaming,
    ]
  );

  // ─── State context value (re-renders on data changes) ─────────────────────────
  const stateValue = useMemo(
    () => ({
      // agents
      agents: agentHook.agents,
      isLoadingAgents: agentHook.isLoadingAgents,
      isLoadingAgentDetails: agentHook.isLoadingAgentDetails,
      agentsError: agentHook.error,
      selectedAgent: agentHook.selectedAgent,
      selectedAgentId: agentHook.selectedAgentId,
      conversationStarters: agentHook.conversationStarters,
      defaultAgentForNewChat,
      defaultAgentIdForNewChat,
      activeScope,

      // messages
      messages: messagesHook.messages,
      agentIsTyping: messagesHook.agentIsTyping,
      isLoadingMessages: messagesHook.isLoadingMessages,
      error: messagesHook.error,

      // conversations
      conversations: conversationsHook.conversations,
      selectedConversationId: conversationsHook.selectedConversationId,
      conversationId: conversationsHook.conversationId,
      isLoadingConversations: conversationsHook.isLoadingConversations,
      isCreatingConversation: conversationsHook.isCreatingConversation,
      isDeletingConversation: deleteHook.isDeleting,

      // auth
      userId: authStore.userId,
      tenantId: authStore.currentWorkspaceUuid || null,

      // document panel
      isDocumentPanelOpen: documentPanelHook.isDocumentPanelOpen,
      documentPanelTitle: documentPanelHook.documentPanelTitle,
      documentPanelContent: documentPanelHook.documentPanelContent,
      isSidebarCollapsed: documentPanelHook.isSidebarCollapsed,
      documents: chatDocumentsHook.documents,
      currentDocumentId: chatDocumentsHook.currentDocumentId,
      currentDocument: chatDocumentsHook.currentDocument,
      sortedDocuments: chatDocumentsHook.sortedDocuments,
      documentCount: chatDocumentsHook.documentCount,
      documentCreatedMessageIdsByConversation,

      // model selector
      providers: modelSelector.providers,
      selectedProvider: modelSelector.selectedProvider,
      selectedModel: modelSelector.selectedModel,

      // conversation search
      isConversationSearchOpen,

      // slide-out
      isSlideOutMode,

      // global chat trigger
      isGlobalChatOpen,

      // ui placeholders
      isChatQuestionsVisible: true,
      dagProgress: [],
      currentDagNode: null,
      isChatReasoningActive: false,
      attachment: null,
      persistentFileUuids: [],

      // streaming state
      currentActivity,
      toolCalls,
      hitlRequest,
      sources,
      uiBlocks,
      messageStatus,
      planSteps,
      isStreaming,
    }),
    [
      defaultAgentForNewChat,
      defaultAgentIdForNewChat,
      activeScope,
      documentCreatedMessageIdsByConversation,
      isConversationSearchOpen,
      isSlideOutMode,
      isGlobalChatOpen,
      // agentHook state
      agentHook.agents,
      agentHook.isLoadingAgents,
      agentHook.isLoadingAgentDetails,
      agentHook.error,
      agentHook.selectedAgent,
      agentHook.selectedAgentId,
      agentHook.conversationStarters,
      // messagesHook state
      messagesHook.messages,
      messagesHook.agentIsTyping,
      messagesHook.isLoadingMessages,
      messagesHook.error,
      // conversationsHook state
      conversationsHook.conversations,
      conversationsHook.selectedConversationId,
      conversationsHook.conversationId,
      conversationsHook.isLoadingConversations,
      conversationsHook.isCreatingConversation,
      // deleteHook state
      deleteHook.isDeleting,
      // auth
      authStore.userId,
      authStore.currentWorkspaceUuid,
      // documentPanelHook state
      documentPanelHook.isDocumentPanelOpen,
      documentPanelHook.documentPanelTitle,
      documentPanelHook.documentPanelContent,
      documentPanelHook.isSidebarCollapsed,
      // chatDocumentsHook state
      chatDocumentsHook.documents,
      chatDocumentsHook.currentDocumentId,
      chatDocumentsHook.currentDocument,
      chatDocumentsHook.sortedDocuments,
      chatDocumentsHook.documentCount,
      // modelSelector state
      modelSelector.providers,
      modelSelector.selectedProvider,
      modelSelector.selectedModel,
      // streaming state
      currentActivity,
      toolCalls,
      hitlRequest,
      sources,
      uiBlocks,
      messageStatus,
      planSteps,
      isStreaming,
    ]
  );

  return (
    <HomeChatProvider actionsValue={actionsValue} stateValue={stateValue}>
      {children}
    </HomeChatProvider>
  );
};
